import { type CompanyEntitlementResponse } from 'types/api/companies/entitlements'
import { type Monitoring } from 'types/api/company-report/company'
import { type RestrictedTimeWindows } from 'types/api/teams'

export enum PrivateNetworkIntegrationScope {
  Team = 'Team',
  Company = 'Company'
}

export interface CompanyOrg {
  active: boolean
  auto_add_users: boolean
  certificate_expiration_imminent: boolean
  certificate_expires_on: string
  certificate_set_at: string
  certificate_set_by: string
  company_id: string
  created_at: string
  identifier: string
  idle: boolean
  name: string
  old_certificate_expiration_imminent?: boolean
  old_certificate_set_at?: string
  old_certificate_set_by?: string
  secret_set_at?: string
  secret_set_by?: string
  updated_at: string
}

export interface CompanyPreferences {
  auto_add_users: boolean
  default_mfa_provider?: string
  default_orgs?: string[]
  domain?: string
  // `extra_credit` is null on the BE by default. When it is null, this value is not returned. For it to have a value of true or false, user has to explicitly opt in or out.
  extra_credit?: boolean
  force_mfa: boolean
  halt_all_moratorium: boolean
  halt_all_moratorium_duration: string // eg. 5m
  jit_provisioning: boolean
  llm_access: boolean
  mfa_providers?: string[]
  monitoring?: Monitoring
  pni_scope: PrivateNetworkIntegrationScope
  production: boolean
  target_all_adhoc?: boolean
  // Companies created before this pref existed have no stored value, so it can be absent.
  test_cooldown_duration?: string // eg. 5m
  test_run_restriction_windows?: RestrictedTimeWindows[]
}

export interface OAuthSettings {
  authorization_uri: string
  client_id: string
  client_secret: string
  enabled: boolean
  scope: string
  token_uri: string
  user_info_uri: string
}

export interface SamlIdpPreferences {
  cert: string
  claims_forced: 'true' | 'false'
  enabled: 'true' | 'false'
  entity_id: string
  forced: 'true' | 'false'
  url: string
}

export interface AuthMethodPreferences {
  allow_session_renewal: boolean
  api_keys_enabled: boolean
  claims_required: boolean
  google_enabled: boolean
  mfa_required: boolean
  oauth_enabled: boolean
  password_enabled: boolean
  saml_aliases_enabled: boolean
  saml_enabled: boolean
  api_keys_max_seconds_to_expire_enabled?: boolean // Optional, as it may not be present in all contexts
  api_keys_max_seconds_to_expire?: number // Optional, as it may not be present in all contexts
  session_duration?: number // Optional, as it may not be present in all contexts
}

export interface Company {
  auth_method_prefs: AuthMethodPreferences
  company_entitlements_response?: CompanyEntitlementResponse
  created_at: string
  created_by: string
  entitlements: CompanyEntitlementResponse
  identifier: string
  is_org_secret_allowed: boolean
  name: string
  o_auth_settings: OAuthSettings
  orgs: CompanyOrg[]
  prefs: CompanyPreferences
  saml_idp_prefs: SamlIdpPreferences
  updated_at: string
  default_company_role: string // this has the type of RbacRoleGremlinInternalCompany | RbacRoleCompany - or string when it is a custom role
  default_team_role: string // this has the type of RbacRoleTeam or string when it is a custom role
}
